import { ref, computed, watch } from "vue";
import { isSeparatorAlbum } from "@/utils/separators";
import { fetchNowPlaying } from "@/services/api";
import { resetCircuitBreaker } from "@/services/fetchWithRetry";
import { LISTEN } from "@/services/host";
import { pideOnAir, buscaEnOnAir, aplanaArtista } from "@/services/onair";
import { getEditorialArtist } from "@/services/editorialArtists";
import { useId3Emision, id3Vivo } from "@/services/id3Emision";
import DOMPurify from "dompurify";
import { PLACEHOLDER as astraPlaceHolder } from "@/services/placeholder";

// Astra FM es la 3. El parámetro sobrevive de cuando hubo más de un canal
// y desaparecerá cuando C4 lo retire del todo; mientras, el valor por defecto
// es el único que existe.
export default function useNowPlaying(stationId = "3") {
  // Refs for now playing data
  const nowPlaying = ref(null);
  const nextPlaying = ref(null);
  const queue = ref([]);
  const activeTrack = ref(null);
  const upcomingTrack = ref(null);
  const isLiveSeparator = ref(false);
  const artistBio = ref("");
  const albumArt = ref("");
  const artistImage = ref("");
  const spotifyArtistProfileUrl = ref("");
  const artistSource = ref("LastFM");
  // El artista que suena, **tal como lo resuelve el servidor**: trayectoria,
  // género, estilo, sello, miembros y enlaces oficiales. Alimenta la ficha de
  // Radio.
  //
  // 🔴 Antes esto eran los `facts` de TheAudioDB, pedidos desde el navegador
  // de cada oyente. Desde el 25.09.26 vienen dentro de `onair.json`, ya
  // decididos con la regla del §2.1: la ficha manda, dato a dato.
  const artistFacts = ref(null);
  const artistSourceName = ref("");
  const imageUrl = ref("");
  const imageSourceName = ref("");
  const isLoading = ref(true);

  // Cache interval constant - 15 segundos para mejor respuesta con el timer
  const CACHE_INTERVAL_NOW_PLAYING = 15 * 1000; // 15 segundos


  // Timer para sincronizar con el fin de la canción
  let songEndTimer = null;
  let lastSongId = null;
  let scheduledSongEndTime = null; // Timestamp de cuando debería terminar la canción programada
  let metadataRequestId = 0;
  let lastResolvedTrackKey = "";
  let lastNonSeparatorTrack = null;

  // §6 del contrato de artistas (ARTISTAS_WEB_INSTRUCCIONES, 23-jul-2026):
  // mientras el mismo artista sigue sonando, la resolución de bio/imagen no se
  // vuelve a invocar (va atada al cambio de canción), así que una edición en
  // caliente del dashboard (priority, texto de bio) no se refleja hasta que
  // cambie el artista o se recargue. Solución: re-resolver periódicamente la
  // metadata del artista activo. El caché de 5 min de editorial.json limita las
  // descargas reales aunque se re-invoque cada ~75s.
  const ARTIST_METADATA_REFRESH_MS = 75 * 1000;
  let artistMetadataRefreshTimer = null;


  function buildTrackViewModel(song, fallbackAlbumArt = "") {
    if (!song) return null;
    return {
      song,
      albumArt: song.art || fallbackAlbumArt || astraPlaceHolder,
      albumName: song.album || "",
      albumYear:
        song.custom_fields?.year || song.custom_fields?.album_year || null,
    };
  }

  function getTrackKey(track) {
    if (!track?.song) return "";
    return [
      track.song.id || "",
      track.song.artist || "",
      track.song.title || "",
      track.albumName || "",
    ].join("|");
  }

  function buildUnifiedPlaybackContext(current, next, queueSongs) {
    const currentTrack = buildTrackViewModel(current?.song, current?.albumArt);
    const currentIsSeparator = isSeparatorAlbum(currentTrack?.albumName);

    if (currentTrack && !currentIsSeparator) {
      lastNonSeparatorTrack = currentTrack;
    }

    const nextTrack = buildTrackViewModel(
      next?.song,
      next?.nextAlbumArt || next?.song?.art,
    );

    const queueCandidates = Array.isArray(queueSongs)
      ? queueSongs
          .map((item) => buildTrackViewModel(item?.song, item?.song?.art))
          .filter(Boolean)
      : [];

    const firstQueuePlayable = queueCandidates.find(
      (track) => !isSeparatorAlbum(track?.albumName),
    );

    let resolvedActiveTrack = null;
    if (!currentIsSeparator) {
      resolvedActiveTrack = currentTrack;
    } else if (nextTrack && !isSeparatorAlbum(nextTrack.albumName)) {
      resolvedActiveTrack = nextTrack;
    } else if (firstQueuePlayable) {
      resolvedActiveTrack = firstQueuePlayable;
    } else if (lastNonSeparatorTrack) {
      resolvedActiveTrack = lastNonSeparatorTrack;
    }

    if (!resolvedActiveTrack) {
      resolvedActiveTrack = currentTrack || null;
    }

    const activeKey = getTrackKey(resolvedActiveTrack);
    const resolvedUpcomingTrack =
      queueCandidates.find((track) => {
        const trackKey = getTrackKey(track);
        return (
          trackKey &&
          trackKey !== activeKey &&
          !isSeparatorAlbum(track.albumName)
        );
      }) || null;

    return {
      currentIsSeparator,
      resolvedActiveTrack,
      resolvedUpcomingTrack,
    };
  }

  // Computed properties
  const sanitizedArtistBio = computed(() => {
    return DOMPurify.sanitize(artistBio.value);
  });

  // Función auxiliar para esperar hasta que cambie la canción con polling
  async function waitForSongChange(expectedOldSongId, maxAttempts = 10) {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const data = await fetchNowPlaying(stationId);
        const currentSongId =
          data.nowPlaying?.song?.id || data.nowPlaying?.sh_id;
        const elapsed = data.nowPlaying?.elapsed || 0;
        const album = data.nowPlaying?.song?.album || "";
        const normalizedAlbum = album.toLowerCase().trim();

        // Detectar si es un separador (shorts, off-general o anuncios)
        const isSeparator =
          normalizedAlbum === "shorts" ||
          normalizedAlbum === "off-general" ||
          normalizedAlbum === "anuncios";

        // Verificar que realmente cambió de canción
        if (currentSongId && currentSongId !== expectedOldSongId) {
          // Validación adicional: la nueva canción debe tener elapsed > 0
          if (elapsed > 0) {
            // Si es un separador, NO retornar - seguir esperando a la canción real
            if (isSeparator) {
              // Continuar al siguiente intento
              if (attempt < maxAttempts) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
              }
              continue;
            }

            // Solo retornar si NO es separador (canción real)
            return data;
          }
        }

        // Si no cambió y no es el último intento, esperar antes de reintentar
        if (attempt < maxAttempts) {
          // Usar un delay más corto (1s) para responder más rápido
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      } catch (error) {
        console.warn("[useNowPlaying] Error en polling:", error);
      }
    }

    return null;
  }

  // Función para programar la actualización cuando termine la canción
  function scheduleSongEndUpdate(
    remainingSeconds,
    songId,
    duration = null,
    elapsed = null,
    isSeparator = false,
  ) {
    // Solo programar si hay tiempo restante válido
    if (remainingSeconds <= 0) {
      return;
    }

    // Calcular remaining del lado del cliente si tenemos duration y elapsed
    let clientCalculatedRemaining = remainingSeconds;
    if (duration && elapsed !== null) {
      clientCalculatedRemaining = duration - elapsed;
      // Usar el remaining calculado del cliente si es válido
      if (clientCalculatedRemaining > 0) {
        remainingSeconds = clientCalculatedRemaining;
      }
    }

    const now = Date.now();

    // Si ya hay un timer programado para la misma canción
    if (songEndTimer && songId === lastSongId && scheduledSongEndTime) {
      const timeUntilScheduledEnd = scheduledSongEndTime - now;
      const newRemainingMs = remainingSeconds * 1000;

      // Si el nuevo remaining es MAYOR que el tiempo hasta el fin programado,
      // significa que Azuracast reportó datos desactualizados - NO reprogramar
      if (newRemainingMs > timeUntilScheduledEnd + 5000) {
        return;
      }

      // Si el nuevo remaining es significativamente menor (>10s de diferencia),
      // puede ser un crossfade o datos más actualizados - reprogramar
      if (timeUntilScheduledEnd - newRemainingMs <= 10000) {
        return;
      }
    }

    // Cancelar timer anterior si existe
    if (songEndTimer) {
      clearTimeout(songEndTimer);
      songEndTimer = null;
    }

    // Actualizar lastSongId si la canción cambió
    if (songId !== lastSongId) {
      scheduledSongEndTime = null;
      lastSongId = songId;
    }

    const msRemaining = remainingSeconds * 1000;

    // Para separadores, usar buffer más corto (1s) para responder más rápido
    // Para canciones, usar buffer de 3s para evitar crossfade prematuro
    const bufferMs = isSeparator ? 1000 : 3000;
    const updateDelay = msRemaining + bufferMs;

    scheduledSongEndTime = now + msRemaining;

    songEndTimer = setTimeout(async () => {
      // Esperar hasta que la canción realmente cambie en Azuracast
      const newData = await waitForSongChange(songId);

      if (newData) {
        await processNowPlayingData(newData, true);
      } else {
        await updateNowPlaying(true);
      }

      songEndTimer = null;
    }, updateDelay);
  }

  // Función para cancelar el timer (útil para cleanup)
  function cancelSongEndTimer() {
    if (songEndTimer) {
      clearTimeout(songEndTimer);
      songEndTimer = null;
      scheduledSongEndTime = null;
    }
    stopArtistMetadataRefresh();
  }

  // §6: detiene el refresco periódico de metadata del artista activo.
  function stopArtistMetadataRefresh() {
    if (artistMetadataRefreshTimer) {
      clearInterval(artistMetadataRefreshTimer);
      artistMetadataRefreshTimer = null;
    }
  }

  // §6: (re)arranca el refresco periódico para el artista que suena ahora.
  //
  // Existe porque la redacción edita fichas en caliente y espera ver el cambio
  // sin recargar (§4 del contrato de artistas). Mientras el mismo artista siga
  // en antena, se vuelve a preguntar por él.
  //
  // ⚠️ Ahora eso es **una petición**, no cuatro: `pideOnAir` baja la respuesta
  // entera y el servidor la sirve con ETag, así que cuando nada ha cambiado
  // contesta 304 sin cuerpo.
  function startArtistMetadataRefresh(artistName, albumImageUrl, trackTitle) {
    stopArtistMetadataRefresh();
    if (!artistName) return;

    artistMetadataRefreshTimer = setInterval(async () => {
      // El guard metadataRequestId ya evita colisiones si justo entra una
      // resolución por cambio de canción: gana la petición con el id más alto.
      metadataRequestId += 1;
      const currentRequestId = metadataRequestId;
      // Se fuerza la petición: el refresco existe precisamente para traer lo
      // que haya cambiado, así que mirar primero en memoria no valdría.
      await pideOnAir();
      await pideArtista(
        artistName,
        albumImageUrl,
        trackTitle,
        currentRequestId,
      );
    }, ARTIST_METADATA_REFRESH_MS);
  }

  /**
   * Pone en pantalla el artista que el servidor ya ha resuelto.
   *
   * 🔴 **Aquí ya no se decide nada.** La cadena «ficha → TheAudioDB → Last.fm»
   * para la bio, y «ficha → TheAudioDB → carátula» para la foto, la aplica el
   * servidor con la regla del §2.1 y nos manda el resultado con un `de` que
   * dice de dónde salió cada dato. Esto solo reparte.
   *
   * ⚠️ **Una bio vacía puede ser la respuesta correcta.** Cuando el servidor
   * tiene dudas de homónimo —nombre distinto en el servicio, país que no cuadra
   * con MusicBrainz, o el artista apartado por la redacción— no usa nada de
   * fuera y manda `bio: null`. Es deliberado: mejor no decir nada que contar la
   * vida de otro grupo, que es lo que pasó con ASTRO. **No se rellena con un
   * respaldo.**
   *
   * @param {object|null} resuelto lo que devuelve `aplanaArtista`
   * @param {string} albumImageUrl la carátula, como último recurso de la foto
   * @param {number} requestId para que una respuesta lenta no pise a la nueva
   */
  // De quién son la foto y la bio que hay en pantalla. Ver `pideArtista`.
  let artistaPintado = "";

  function aplicaArtista(resuelto, albumImageUrl = null, requestId = 0) {
    if (requestId && requestId !== metadataRequestId) return;

    if (!resuelto) {
      // Sin dato no se vacía la pantalla: ver `pideArtista`.
      return;
    }

    artistaPintado = resuelto.nombre || "";

    artistImage.value = resuelto.imagen || albumImageUrl || astraPlaceHolder;
    imageUrl.value = resuelto.imagenEnlace || "";
    imageSourceName.value =
      resuelto.imagenCredito || (resuelto.imagen ? "" : "Álbum");

    // La bio llega entera; el recorte a 798 es nuestro y sigue siendo nuestro.
    let bio = resuelto.bio || "";
    if (bio.length > 798) bio = bio.substring(0, 798) + "...";

    if (artistBio.value !== bio) artistBio.value = bio;
    if (artistSource.value !== (resuelto.bioEnlace || "")) {
      artistSource.value = resuelto.bioEnlace || "";
    }
    if (artistSourceName.value !== (resuelto.bioCredito || "")) {
      artistSourceName.value = resuelto.bioCredito || "";
    }

    artistFacts.value = resuelto;

    // ⚠️ Spotify se fue del navegador hace tiempo —429 constantes— y lo que
    // queda es el hueco del enlace, que hoy lo cubre `links.spotify` de la
    // ficha. Se deja a cero para no arrastrar un valor del artista anterior.
    spotifyArtistProfileUrl.value = "";
  }

  /**
   * Trae el artista de la canción que suena.
   *
   * Primero mira en lo que ya tenemos: `onair.json` viene con las **cuatro
   * siguientes** ya resueltas, así que el cambio de canción normal no cuesta
   * ninguna petición. Solo se pide cuando la canción no está, que es lo que
   * pasa al arrancar o si la emisión se salta la cola.
   *
   * ⚠️ **Si el servidor no responde, no se toca la pantalla.** El §3 del
   * contrato lo dice, y el aviso del 25.09.26 nombra el caso concreto: tras un
   * despliegue suyo, `onair.json` puede dar **502 durante un minuto**. Vaciar
   * el bloque por eso sería cambiar un minuto de datos viejos por un minuto de
   * nada. Y **no se vuelve a TheAudioDB**: ese camino ya no existe.
   */
  async function pideArtista(
    artistName,
    albumImageUrl = null,
    trackTitle = null,
    requestId = 0,
  ) {
    if (!artistName) return;

    let cancion = buscaEnOnAir(artistName, trackTitle);
    if (!cancion) {
      await pideOnAir();
      cancion = buscaEnOnAir(artistName, trackTitle);
    }

    let resuelto = aplanaArtista(cancion);

    // 🔴 **Si `onair.json` no lo tiene, la ficha del artista.** Pasa al abrir la
    // web a media canción: el servidor va por delante y la que se oye puede no
    // haber pasado nunca por el fichero de este navegador. La ficha trae lo
    // curado —foto y bio de la redacción—, que es lo que manda (§2.1).
    if (!resuelto) {
      const ficha = await getEditorialArtist(artistName).catch(() => null);
      if (ficha && !ficha.pendiente && (ficha.bio || ficha.imageUrl)) {
        resuelto = {
          ...artistaVacio(artistName),
          enBiblioteca: true,
          imagen: ficha.imageUrl || "",
          imagenCredito: ficha.imageCredit || "",
          imagenEnlace: ficha.imageSource || "",
          bio: ficha.bio || "",
          bioCredito: ficha.bioCredit || "",
          bioEnlace: ficha.bioSource || "",
          genero: ficha.genero || "",
          links: ficha.links || null,
        };
      }
    }

    // 🔴 **Y si no hay nada, no se deja al artista de antes.** «Sin dato no se
    // toca la pantalla» es para cuando el servidor falla un momento con el
    // mismo artista sonando; si el artista ha cambiado, dejar su foto y su bio
    // es enseñar a otro grupo con el nombre de este (Sleaford Mods con la bio de
    // Bowery Electric, 03.10.26). Mejor la carátula y ninguna bio.
    if (!resuelto && mismoNombre(artistaPintado, artistName)) return;

    aplicaArtista(resuelto || artistaVacio(artistName), albumImageUrl, requestId);
  }

  function mismoNombre(a, b) {
    return String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
  }

  // La forma de `aplanaArtista` sin ningún dato: la carátula hará de retrato y
  // los bloques de la ficha se quedan vacíos.
  function artistaVacio(nombre) {
    return {
      slug: "",
      nombre,
      enBiblioteca: false,
      imagen: "",
      imagenDe: "",
      imagenCredito: "",
      imagenEnlace: "",
      bio: "",
      bioDe: "",
      bioCredito: "",
      bioEnlace: "",
      trayectoria: null,
      genero: "",
      estilo: "",
      sello: "",
      miembros: null,
      links: null,
    };
  }

  // ── El cambio lo marca el audio, no el reloj ──────────────────────────
  //
  // 🔴 **Porque nadie oye al servidor.** La emisión llega a cada aparato con
  // un colchón de entre 12 y más de medio minuto según la red, así que atar la
  // ficha al reloj del servidor falla siempre y en direcciones distintas: con
  // detección inmediata cambia antes de que se oiga, y con detección lenta
  // después. Medido el 25.09.26 en una grabación de Pablo: 43 segundos tarde.
  //
  // Los ID3 de la emisión van pegados al audio, así que esto cambia **cuando
  // suena el cambio**, para cada oyente, con su retraso.
  //
  // ⚠️ **Esto no sustituye al sondeo, lo adelanta.** El temporizador de
  // `scheduleSongEndUpdate` y el refresco de la vista siguen ahí: son los que
  // traen la cola, el historial y lo que viene después, y los que sostienen la
  // pantalla cuando no hay audio sonando —alguien que abre la web y no le da
  // al play no tiene ID3 que escuchar—. Lo que hace esto es pintar el cambio
  // en el instante bueno en vez de esperar al siguiente sondeo.
  const { loQueSuena } = useId3Emision();

  // Lo último que se buscó y no estaba, para no pedir el JSON dos veces por lo
  // mismo mientras dure ese tramo de audio.
  let ultimoNoEncontrado = "";
  // El ID3 trajo una canción que no está en `onair.json`: entonces la pantalla
  // no puede seguir al audio y vuelve a mandar el sondeo.
  let id3SinEncaje = false;

  watch(loQueSuena, async (id3) => {
    if (!id3?.artista) return;

    // ⚠️ **Las cuñas también llevan ID3.** Medido el 26.09.26: una cortinilla
    // emite `TPE1: Crepusculo`, `TIT2: crepusculo-off-1`, `TALB: shorts`. No
    // están en `onair.json` —el contrato las excluye— así que buscarlas sería
    // pedir el JSON otra vez, en cada cuña, para no encontrar nada. Se
    // reconocen por el álbum, con la misma regla que el resto de la casa, y se
    // ignoran: durante la cuña sigue en pantalla la última canción, que es lo
    // que ya hacía `buildUnifiedPlaybackContext`.
    if (isSeparatorAlbum(id3.disco)) return;

    const clave = `${id3.artista}|${id3.titulo}`;

    let cancion = buscaEnOnAir(id3.artista, id3.titulo);
    if (!cancion && clave !== ultimoNoEncontrado) {
      // El contrato lo prevé: si el ID3 trae algo que no está en `onair.json`
      // —solo con un retraso enorme— se vuelve a pedir. Una vez.
      ultimoNoEncontrado = clave;
      await pideOnAir();
      cancion = buscaEnOnAir(id3.artista, id3.titulo);
    }
    if (!cancion) {
      // No se ha podido pintar lo que dice el audio: que mande el sondeo.
      id3SinEncaje = true;
      return;
    }
    id3SinEncaje = false;
    ultimoNoEncontrado = "";

    // Ya está pintado: no se toca nada. Sin esta guarda, cada `cue` del mismo
    // tramo repintaría la ficha entera.
    const yaEs =
      activeTrack.value?.song?.artist === cancion.artist &&
      activeTrack.value?.song?.title === cancion.title;
    if (yaEs) return;

    activeTrack.value = buildTrackViewModel(
      {
        id: cancion.played_at,
        artist: cancion.artist,
        title: cancion.title,
        album: cancion.album,
        art: cancion.art,
        custom_fields: { year: cancion.year },
      },
      cancion.art,
    );
    if (cancion.art) albumArt.value = cancion.art;

    // El artista ya viene resuelto dentro de la misma respuesta: pintarlo no
    // cuesta ninguna petición.
    metadataRequestId += 1;
    aplicaArtista(aplanaArtista(cancion), cancion.art, metadataRequestId);
  });

  // Procesar datos de nowPlaying (lógica extraída para reutilización)
  async function processNowPlayingData(data, skipCache = false) {
    const cacheKey = `nowPlaying_${stationId}`;
    const timestampKey = `nowPlayingTimestamp_${stationId}`;
    const now = Date.now();

    if (data.nextPlaying) {
      nextPlaying.value = data.nextPlaying;
    }
    if (data.queue) {
      queue.value = data.queue;
    }
    if (data && data.nowPlaying) {
      const currentSongId = data.nowPlaying.song?.id || data.nowPlaying.sh_id;
      const hasChangedSong = lastSongId !== currentSongId;

      // Si detectamos un cambio de canción pero el timer indica que aún falta tiempo,
      // probablemente sea crossfade - NO actualizar hasta que el timer se dispare
      if (hasChangedSong && scheduledSongEndTime) {
        const timeUntilScheduledEnd = scheduledSongEndTime - Date.now();

        // Si faltan más de 5 segundos para el fin programado, es crossfade prematuro
        if (timeUntilScheduledEnd > 5000) {
          // Mantener los datos actuales, no actualizar nada
          isLoading.value = false;
          return;
        }
      }

      // Only update nowPlaying if the structure is correct
      nowPlaying.value = data.nowPlaying;

      // NO limpiar scheduledSongEndTime aquí - se sobrescribirá cuando se programe el nuevo timer
      // Esto asegura que SIEMPRE haya protección contra crossfade

      // Guardar en caché (a menos que se indique skipCache)
      if (!skipCache) {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            nowPlaying: data.nowPlaying,
            nextPlaying: data.nextPlaying,
            queue: data.queue,
          }),
        );
        localStorage.setItem(timestampKey, now.toString());
      }

      // Detectar si la canción actual es un separador y resolver el contexto unificado
      const unifiedContext = buildUnifiedPlaybackContext(
        data.nowPlaying,
        data.nextPlaying,
        data.queue,
      );
      const isCurrentSongSeparator = unifiedContext.currentIsSeparator;
      // 🔴 **Mientras suena la emisión, la canción la dice el audio** (24.10.26).
      // El sondeo va con el reloj del servidor, de 12 a más de 40 s por delante
      // de lo que oye cada uno, y pisaba lo que acababa de pintar el ID3: se
      // vio a Black Marble y Client turnándose en pantalla durante la misma
      // canción —con la foto, la bio y el visor de Radio cambiando con ellas—
      // hasta que el ID3 volvía a corregirlo. Con ID3 vivo, el sondeo trae la
      // cola y lo que viene, y la canción no la toca. Sin audio (nadie ha dado
      // al play, o en pausa) manda el sondeo, como siempre.
      const mandaElAudio = id3Vivo() && !id3SinEncaje && !!activeTrack.value?.song?.artist;
      if (!mandaElAudio) activeTrack.value = unifiedContext.resolvedActiveTrack;
      upcomingTrack.value = unifiedContext.resolvedUpcomingTrack;
      isLiveSeparator.value = isCurrentSongSeparator;

      if (activeTrack.value?.albumArt) {
        albumArt.value = activeTrack.value.albumArt;
      }

      // Programar próxima actualización cuando termine la canción
      const remainingSeconds = data.nowPlaying.remaining;
      const duration = data.nowPlaying.duration;
      const elapsed = data.nowPlaying.elapsed;

      if (remainingSeconds !== undefined) {
        scheduleSongEndUpdate(
          remainingSeconds,
          currentSongId,
          duration,
          elapsed,
          isCurrentSongSeparator,
        );
      }

      // Actualizar bundle completo (foto+bio+fuentes) según canción visible unificada.
      const metadataTrack = activeTrack.value;
      const metadataTrackKey = getTrackKey(metadataTrack);
      const metadataArtist = metadataTrack?.song?.artist;

      const needsArtistInfo =
        !!metadataArtist &&
        (metadataTrackKey !== lastResolvedTrackKey ||
          !artistImage.value ||
          (!artistBio.value && metadataArtist));

      if (needsArtistInfo) {
        lastResolvedTrackKey = metadataTrackKey;
        metadataRequestId += 1;
        const currentRequestId = metadataRequestId;

        const metadataAlbumArt =
          metadataTrack?.albumArt || data.nowPlaying.albumArt;
        const metadataTitle =
          metadataTrack?.song?.title || data.nowPlaying.song.title;

        await Promise.all([
          pideArtista(
            metadataArtist,
            metadataAlbumArt,
            metadataTitle,
            currentRequestId,
          ),
        ]);

        // §6: reinicia el refresco periódico para este artista (limpia el
        // intervalo anterior en cada cambio real de artista/track).
        startArtistMetadataRefresh(
          metadataArtist,
          metadataAlbumArt,
          metadataTitle,
        );
      }
    } else {
      // If the data doesn't have the expected structure, handle the case appropriately
      nowPlaying.value = { song: {} }; // Ensure song is present even if empty
    }
    isLoading.value = false;
  }

  // Update now playing information
  async function updateNowPlaying(forceUpdate = false) {
    // Mientras no haya ficha pintada, `onair.json` corre en paralelo y pinta si
    // llega antes (ver § La primera carga). Va deduplicado en `pideOnAir`.
    if (!activeTrack.value) {
      pideOnAir()
        .then((datos) => pintaDesdeOnAir(datos?.ahora))
        .catch(() => {});
    }

    const cacheKey = `nowPlaying_${stationId}`;
    const timestampKey = `nowPlayingTimestamp_${stationId}`;
    const lastFetch = localStorage.getItem(timestampKey);
    const now = Date.now();

    try {
      // Check if data is in cache and not older than 15 seconds
      let data = null;
      if (
        !forceUpdate &&
        lastFetch &&
        now - parseInt(lastFetch) < CACHE_INTERVAL_NOW_PLAYING
      ) {
        // If data is in cache and recent, try to retrieve it
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
          try {
            const parsed = JSON.parse(cachedData);
            // Verificar que el caché tenga datos válidos
            if (parsed && parsed.nowPlaying) {
              // Pasar por processNowPlayingData para aplicar verificación de crossfade
              await processNowPlayingData(parsed, true);
              return;
            }
          } catch (e) {
            console.warn("[useNowPlaying] Error parsing cache", e);
          }
        }
      }

      // Si no hay caché o es viejo, hacer llamada HTTP
      data = await fetchNowPlaying(stationId);

      // Procesar los datos
      await processNowPlayingData(data, false);
      reintentosRapidos = 0;
    } catch (error) {
      console.error("Error updating now playing information:", error);
      reintentaPronto();
      // Intentar usar caché antiguo en caso de error
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          if (parsed && parsed.nowPlaying) {
            nowPlaying.value = parsed.nowPlaying;
            if (parsed.nextPlaying) nextPlaying.value = parsed.nextPlaying;
            if (parsed.queue) queue.value = parsed.queue;
          }
        } catch (e) {
          nowPlaying.value = { song: {} };
        }
      } else {
        nowPlaying.value = { song: {} };
      }
      isLoading.value = false;
    }
  }

  // ── La primera carga ──────────────────────────────────────────────────
  //
  // 🔴 **Tardaba hasta un minuto si fallaba la primera petición** (Pablo,
  // 08.10.26). Medido: un corte de solo 5 s en `/api/nowplaying` al abrir Radio
  // dejaba la ficha vacía 31,6 s, porque el siguiente intento era el refresco de
  // 30 s; si ese también fallaba, el minuto. Pasa en incógnito y a quien entra
  // por primera vez igual que a cualquiera. Dos arreglos:
  //
  //   1. **La ficha se pinta con `onair.json` si llega antes**, que ya viene
  //      resuelto por el servidor —artista, canción, foto, bio, carátula—.
  //      AzuraCast sigue mandando en el reloj de la canción (`elapsed`,
  //      `duration`), que `onair.json` no da con esa precisión.
  //   2. **Si la petición a AzuraCast falla, se reintenta a los 2, 4 y 8 s**,
  //      no al refresco de 30. Antes de cada reintento se reabre el cortacircuitos
  //      de `fetchWithRetry`: con tres reintentos seguidos se abriría solo y
  //      bloquearía el servidor un minuto, que es justo lo que se quiere evitar.
  const PAUSAS_RAPIDAS = [2000, 4000, 8000];
  let reintentosRapidos = 0;
  let reintentoTimer = null;

  function reintentaPronto() {
    if (reintentosRapidos >= PAUSAS_RAPIDAS.length || reintentoTimer) return;
    const pausa = PAUSAS_RAPIDAS[reintentosRapidos];
    reintentosRapidos += 1;
    reintentoTimer = setTimeout(() => {
      reintentoTimer = null;
      resetCircuitBreaker(LISTEN);
      updateNowPlaying(true);
    }, pausa);
  }

  // La ficha desde `onair.json`, solo si AzuraCast no ha pintado ya: la misma
  // ruta que el cambio de canción por ID3.
  function pintaDesdeOnAir(ahora) {
    if (activeTrack.value || !ahora?.artist) return;
    activeTrack.value = buildTrackViewModel(
      {
        id: ahora.played_at,
        artist: ahora.artist,
        title: ahora.title,
        album: ahora.album,
        art: ahora.art,
        custom_fields: { year: ahora.year },
      },
      ahora.art,
    );
    if (ahora.art) albumArt.value = ahora.art;
    metadataRequestId += 1;
    aplicaArtista(aplanaArtista(ahora), ahora.art, metadataRequestId);
    isLoading.value = false;
  }

  // Initialize data
  async function initialize() {
    await updateNowPlaying();
  }

  return {
    nowPlaying,
    nextPlaying,
    queue,
    activeTrack,
    upcomingTrack,
    isLiveSeparator,
    artistBio,
    albumArt,
    artistImage,
    spotifyArtistProfileUrl,
    artistSource,
    artistSourceName,
    artistFacts,
    imageUrl,
    imageSourceName,
    isLoading,
    sanitizedArtistBio,
    updateNowPlaying,
    pideArtista,
    isSeparatorAlbum,
    initialize,
    cancelSongEndTimer,
  };
}
